import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoadingOverlay } from '../elements/LoadingOverlay';
import type { API } from '@/api';
import { LoungeLogo } from '@/components/elements/LoungeLogo';
import { ROUTE_PATH } from '@/constants/routes';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { useDialog } from '@/hooks/useDialog';
import { useFloorFormat } from '@/hooks/useFloorFormat';
import { useNameFormat } from '@/hooks/useNameFormat';
import { getPersonalDeviceFloorMapImagePath } from '@/utils/floorMapPath';

type ReservationData =
  API['POST']['CreateReservation']['response']['reservation'];

export const ReservationComplete = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { translateApiError } = useApiErrorTranslation();

  const [error, setError] = useState<string | null>(null);

  const {
    dialogRef: mapDialogRef,
    open: openMapDialog,
    close: closeMapDialog,
  } = useDialog();
  const {
    dialogRef: cancelDialogRef,
    open: openCancelDialog,
    close: closeCancelDialog,
  } = useDialog();
  const {
    dialogRef: cancelCompleteDialogRef,
    open: openCancelCompleteDialog,
    close: closeCancelCompleteDialog,
  } = useDialog();

  const { formatDateWithWeekday, formatTimeJst } = useDateTimeFormat();
  const { formatFloor } = useFloorFormat();
  const { formatName } = useNameFormat();

  const { execute: cancelReservation, isRequesting } = useApiRequest(
    'DELETE',
    API_ENDPOINTS.DELETE.CANCEL_RESERVATION,
    { showGlobalLoading: false },
  );

  const reservation = location.state?.reservation as
    | ReservationData
    | undefined;

  // 予約データがない場合は予約画面に戻す
  useEffect(() => {
    if (!reservation) {
      navigate(ROUTE_PATH.RESERVATION);
    }
  }, [reservation, navigate]);

  if (!reservation) {
    return null;
  }

  // 利用時間（分）を計算
  const calculateDuration = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  };

  const duration = calculateDuration(
    reservation.start_time,
    reservation.end_time,
  );

  const handleCancelConfirm = async () => {
    closeCancelDialog();

    const result = await cancelReservation({
      id: reservation.id,
    });

    if (result.ok) {
      openCancelCompleteDialog();
    } else {
      const { message } = translateApiError(result.error);
      setError(message);
    }
  };

  const handleCancelCompleteConfirm = () => {
    closeCancelCompleteDialog();
    const serviceType = reservation.space_type;
    navigate(`${ROUTE_PATH.RESERVATION}?service=${serviceType}`);
  };

  const floorMapPath = getPersonalDeviceFloorMapImagePath(reservation);

  return (
    <div className="page p-auth page_b">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                {/* ヒーロー */}
                <div className="l-hero">
                  <img
                    className="l-hero__img"
                    src="/Common_Title.jpg"
                    alt="LOUNGE RESERVATION"
                  />
                </div>

                {/* エラー表示 */}
                {error && <div className="common-error">{error}</div>}

                {/* エリア群（グリッド） */}
                <div className="l-grid">
                  {/* ボックス1：予約内容確認 */}
                  <section className="l-card">
                    <div className="l-card__body">
                      <div className="reservation-card__complete-message">
                        {t('reservation.complete.thankYouMessage')}
                      </div>
                      <div
                        className={`l-box ${reservation.space_type === 'shower_room' ? 'l-box--shower' : 'l-box--booth'} reservation-detail`}
                      >
                        <div className="reservation-detail__section reservation-detail__list">
                          {/* 氏名 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__name">
                              {formatName(
                                reservation.last_name,
                                reservation.first_name,
                              )}
                            </div>
                          </div>

                          {/* 日付 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__date">
                              {formatDateWithWeekday(reservation.start_time)}
                            </div>
                          </div>

                          {/* 時間 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__time">
                              {formatTimeJst(reservation.start_time)}
                              {t('reservation.confirm.timeRange')}
                              {formatTimeJst(reservation.end_time)}
                              {t('reservation.confirm.durationFormat', {
                                duration,
                              })}
                            </div>
                          </div>
                          {/* タイムゾーン注記 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-card__info reservation-card__info--timezone-left">
                              {t('reservation.boarding.timezoneNote')}
                            </div>
                          </div>

                          {/* 空港 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__airport">
                              {t(`common.airport.${reservation.airport}`)}
                              <button
                                className="reservation-detail__btn reservation-detail__btn--map js-open-dialog"
                                type="button"
                                onClick={openMapDialog}
                              >
                                MAP
                              </button>
                            </div>
                          </div>

                          {/* ターミナル */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__terminal">
                              {t(`common.terminal.${reservation.terminal}`)}
                              {t(
                                `common.flightType.${reservation.flight_type}`,
                              )}
                            </div>
                          </div>

                          {/* ラウンジ */}
                          <div className="reservation-detail__row reservation-detail__row--lounge">
                            <div className="reservation-detail__desc reservation-detail__lounge">
                              <div className="reservation-detail__lounge-inner">
                                <LoungeLogo
                                  lounge={reservation.lounge}
                                  className="reservation-detail__lounge-logo"
                                  altText={t(
                                    `common.lounge.${reservation.lounge}`,
                                  )}
                                />
                                {reservation.floor && (
                                  <div className="reservation-detail__lounge-floor">
                                    {formatFloor(reservation.floor)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 予約番号 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__term">
                              {t('reservation.complete.reservationNumber')}
                            </div>
                            <div className="reservation-detail__desc reservation-detail__reservation">
                              <span className="reservation-detail__code">
                                {reservation.reservation_no}
                              </span>
                            </div>
                          </div>

                          {/* 部屋（シャワールームまたは防音個室ブース） */}
                          {reservation.space_name && (
                            <div className="reservation-detail__row reservation-detail__row--room">
                              <div className="reservation-detail__desc reservation-detail__room">
                                <div className="reservation-detail__room-inner">
                                  <div className="reservation-detail__room-img">
                                    <img
                                      className="reservation-detail__room-icon"
                                      src={
                                        reservation.space_type === 'shower_room'
                                          ? '/Common_Reservation_Shower.png'
                                          : '/Common_Reservation_Booth.png'
                                      }
                                      alt={
                                        reservation.space_type === 'shower_room'
                                          ? t('reservation.serviceType.shower')
                                          : t('reservation.serviceType.booth')
                                      }
                                    />
                                  </div>
                                  <div className="reservation-detail__room-no">
                                    No.
                                    <span className="reservation-detail__room-no-number">
                                      {reservation.space_no}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* アクションエリア */}
                      <div className="reservation-card__actions-item">
                        <Link
                          to={ROUTE_PATH.RESERVATION_HISTORY}
                          className="reservation-card__action-link reservation-card__action-link--history"
                        >
                          {t('reservation.complete.historyButton')}
                        </Link>
                      </div>
                      <div className="reservation-card__actions-item">
                        <button
                          className="reservation-card__action-link reservation-card__action-link--cancel"
                          type="button"
                          onClick={openCancelDialog}
                          disabled={isRequesting}
                        >
                          {isRequesting
                            ? t('reservation.complete.canceling')
                            : t('reservation.complete.cancelButton')}
                        </button>
                        <div className="reservation-card__note">
                          {t('reservation.complete.cancelNote')}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* マップダイアログ */}
      <dialog className="dialog dialog--map" ref={mapDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <button
              className="dialog__btn--close js-close-dialog"
              type="button"
              onClick={closeMapDialog}
            >
              ×
            </button>
            <div className="dialog__map-image">
              <img
                src={floorMapPath}
                alt={`${t(`common.terminal.${reservation.terminal}`)} ${t(`common.airport.${reservation.airport}`)} ${t(`common.lounge.${reservation.lounge}`)}`}
              />
            </div>
          </div>
        </div>
      </dialog>

      {/* キャンセル確認ダイアログ */}
      <dialog className="dialog dialog--cancel" ref={cancelDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <div className="dialog__text">
              <p className="dialog__text-line">
                {t('reservation.complete.cancelDialog.message')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </p>
            </div>
            <div className="dialog__actions">
              <button
                className="dialog__btn dialog__btn--yes js-bt-dialog"
                type="button"
                onClick={handleCancelConfirm}
              >
                {t('reservation.complete.cancelDialog.confirmButton')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </button>
              <button
                className="dialog__btn dialog__btn--no js-bt-dialog"
                type="button"
                onClick={closeCancelDialog}
              >
                {t('reservation.complete.cancelDialog.cancelButton')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ローディングオーバーレイ */}
      <LoadingOverlay
        open={isRequesting}
        message={t('reservation.complete.canceling')}
      />

      {/* キャンセル完了ダイアログ */}
      <dialog className="dialog dialog--cancel02" ref={cancelCompleteDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <div className="dialog__text">
              <p className="dialog__text-line">
                {t('reservation.complete.cancelCompleteDialog.message')}
              </p>
            </div>
            <div className="dialog__actions">
              <button
                className="dialog__btn dialog__btn--history js-bt-dialog"
                type="button"
                onClick={handleCancelCompleteConfirm}
              >
                {t('reservation.complete.cancelCompleteDialog.okButton')}
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};
